<script setup>
// =============== 1. 초기 설정 ===============
import { useAnalysisSajuContext } from '@/composables/saju-insight/useAnalysisSajuContext';
import { useStore } from 'vuex';
import { useGesture } from '@/composables/calendar/useGesture';
import { DEV_MODE } from '@/config/dev-mode';
import axios from 'axios';

// Composables
import { useSajuData } from '@/composables/saju-insight/useSajuData';
import { useDialogManager } from '@/composables/useDialogManager';
import { isObjectEmpty, formatNumber } from '@/composables/functions';
import { useTicketProduct } from '@/composables/saju-insight/useTicketProduct';
import { useBackLayer, useBackReducer } from '@/composables/useAppBack';

// Store
import { useMobileSajuLnbStore } from '@/store/modules/mobileSajuLnb';
import { usePaymentsStore } from '@/store/modules/payments';
import { useAnalysisStore } from '@/store/modules/analysis';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { usePrimarySajuStore } from '@/store/modules/primarySaju';

// API
import { fetchHarmonyServiceAPI } from '@/api/baseServiceApi';
import { transformHarmonyResponse } from '@/utils/harmonyDataTransformer';
import { getAnalysisContentAPI, getAnalysisLogAPI, postAnalysisLogAPI } from '@/api/AnalysisApi';
import { getDetail as getGuestUserDetail } from '@/api/UserGroup';
import { postDailyFortuneRateAPI, getDailyFortuneRateAPI } from '@/api/calendarApi';
import { addContentLogAPI } from '@/api/logServiceApi';

// Components
import HarmonyCover from '@/components/saju-insight/nangman-harmony/HarmonyCover.vue';
import HarmonyServiceSelect from '@/components/saju-insight/nangman-harmony/HarmonyServiceSelect.vue';
import ContentsHarmony01 from '@/components/saju-insight/nangman-harmony/ContentsHarmony01.vue';
import ContentsHarmony02 from '@/components/saju-insight/nangman-harmony/ContentsHarmony02.vue';
import ContentsHarmony03 from '@/components/saju-insight/nangman-harmony/ContentsHarmony03.vue';
import ContentsHarmony04 from '@/components/saju-insight/nangman-harmony/ContentsHarmony04.vue';
import AnalysisPaymentInfoBar from '@/components/analysis/common/AnalysisPaymentInfoBar.vue';
import AnalysisMobileHeader from '@/components/analysis/AnalysisMobileHeader.vue';
import ConfirmComponentPay from '@/components/saju-insight/common/confirmComponent.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';
import AlertComponent from '@/components/common/Alert.vue';
import AnalysisLoadingOverlay from '@/components/saju-insight/common/AnalysisLoadingOverlay.vue';
import PopAnalysisInfo from '@/components/saju-insight/common/PopAnalysisInfo.vue';
import IconClose01 from '@/components/svg/IconClose01.vue';
import IconList from '@/components/svg/IconList.vue';

definePage({ meta: { layout: 'm-saju-insight' } });

// 콘텐츠 컴포넌트가 전체 너비로 렌더링하도록 provide
provide('fullWidthMode', true);

const ANALYSIS_TYPE_ID = 38;
/**
 * 모바일 페이지 구조: 각 PC 콘텐츠(좌/우)를 별도 페이지로 분리
 * 종합조언/활동추천 데이터가 없는 경우(3인/4인) 해당 페이지를 동적으로 제외
 *
 * [전체 모드 - 8페이지]
 * Page 1: Content1 좌 (인물관계도)
 * Page 2: Content1 우 (카테고리별 결과)
 * Page 3: Content2 좌 (지표 소개)
 * Page 4: Content2 우 (6개 지표 상세)
 * Page 5: Content3 좌 (종합 조언)
 * Page 6: Content3 우 (활동/분위기)
 * Page 7: Content4 좌 (낭만만세력 소개)
 * Page 8: Content4 우 (설문/새 궁합 보기)
 *
 * [축소 모드 - 6페이지] (3인/4인: 종합조언·활동추천 미지원)
 * Page 1~4: 동일
 * Page 5: Content4 좌 (낭만만세력 소개)
 * Page 6: Content4 우 (설문/새 궁합 보기)
 */

/** 종합조언/활동추천 페이지 표시 여부 (3인/4인은 아직 미지원) */
const showAdvicePage = computed(() => {
  const hData = dataStore.harmonyData;
  return !!(hData?.종합조언 || hData?.함께하면좋은활동);
});

/** 동적 페이지 매핑 */
const dynamicPageMap = computed(() => {
  if (showAdvicePage.value) {
    return {
      1: { content: 1, side: 'left' },
      2: { content: 1, side: 'right' },
      3: { content: 2, side: 'left' },
      4: { content: 2, side: 'right' },
      5: { content: 3, side: 'left' },
      6: { content: 3, side: 'right' },
      7: { content: 4, side: 'left' },
      8: { content: 4, side: 'right' },
    };
  }
  return {
    1: { content: 1, side: 'left' },
    2: { content: 1, side: 'right' },
    3: { content: 2, side: 'left' },
    4: { content: 2, side: 'right' },
    5: { content: 4, side: 'left' },
    6: { content: 4, side: 'right' },
  };
});

const totalPages = computed(() => Object.keys(dynamicPageMap.value).length);

const getContentIndex = (page) => dynamicPageMap.value[page]?.content ?? 1;

/** 모바일 페이지가 좌측(first)인지 우측(second)인지 */
const isLeftSection = computed(() => dynamicPageMap.value[currentPage.value]?.side === 'left');

/** 동적 모바일 목차 */
const dynamicMobileContents = computed(() => {
  if (showAdvicePage.value) {
    return [
      { id: 1, title: '1. 궁합 종합 분석' },
      { id: 3, title: '2. 지표 상세 분석' },
      { id: 5, title: '3. 종합 조언' },
      { id: totalPages.value, title: '4. 마무리' },
    ];
  }
  return [
    { id: 1, title: '1. 궁합 종합 분석' },
    { id: 3, title: '2. 지표 상세 분석' },
    { id: totalPages.value, title: '' },
  ];
});

const context = useAnalysisSajuContext();
const route = useRoute();
const router = useRouter();
const vuexStore = useStore();
const userId = ref(vuexStore.getters['user/id']);

const mobileSajuLnbStore = useMobileSajuLnbStore();
const paymentsStore = usePaymentsStore();
const analysisStore = useAnalysisStore();
const dataStore = useAnalysisDataStore();
const primarySajuStore = usePrimarySajuStore();

const { sajuData } = storeToRefs(dataStore);

// =============== 2. Alert / Dialog ===============
const { alertRef, dialogTitle, dialogText, dialogNextUrl, showAlert } = useDialogManager();

const dialogConfirm = ref(null);
const dialogAlert = ref(null);
const confirmTitle = ref('');
const confirmTitle2 = ref('');
const confirmText = ref('');
const confirmNextUrl = ref(null);
const confirmDoUrl = ref(null);
const warningTxt = ref('');
const alertTitle = ref('');
const alertText2 = ref('');
const alertNextUrl2 = ref(null);

// =============== 3. 웹뷰 통신 ===============
const accessToken = ref(null);
const authUserId = ref(null);
const guestUserId = ref(null);
const isInitialized = ref(false);


// =============== 4. 사주 데이터 ===============
const { initWithValidation } = useSajuData();

// =============== 5. 궁합 로그 데이터 ===============
const harmonyLogData = ref([]);

const fetchHarmonyLogs = async (category = null) => {
  try {
    await analysisStore.getAnalysisLog(context?.saju.value?.guestUserId || null, ANALYSIS_TYPE_ID, category);
    const cacheKey = category ? `${ANALYSIS_TYPE_ID}_${category}` : ANALYSIS_TYPE_ID;
    harmonyLogData.value = analysisStore.analysisLogByType[cacheKey] || [];
  } catch (e) {
    DEV_MODE && console.error('[궁합 모바일] 로그 조회 실패:', e);
  }
};

// =============== 6. 궁합 데이터 fetch ===============
const { harmonyPaymentData } = storeToRefs(analysisStore);
const isPurchased = computed(() => analysisStore.harmonyIsPurchased);
/** 구독이 이 조합을 커버: 구독중 && base(본인 자리)가 내사주.
 *  BE 구독 커버가 base(guestUserId) 기준이라 FE도 base로 판정. */
const subscriptionCovers = computed(
  () => paymentsStore.isSubscribed && primarySajuStore.isMySaju(harmonyParams.value?.guestUserId),
);
/** 표시용 권한: 구매 OR 구독 커버 → 전체 열람 가능 */
const canViewFull = computed(() => isPurchased.value || subscriptionCovers.value);
const isFetchingHarmony = ref(false);

/** 현재 결제 데이터에서 API 파라미터 추출 (PC와 동일) */
const harmonyParams = computed(() => {
  const paymentData = harmonyPaymentData.value;
  if (!paymentData?.category || !paymentData?.persons?.length) return null;

  const sorted = [...paymentData.persons].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const myId = Number(sorted[0].guestUserId);
  const memberIds = sorted.map((p) => Number(p.guestUserId));

  return {
    guestUserId: myId,
    category: paymentData.category,
    memberIds,
    analysisLogId: analysisStore.harmonyMatchedLog?.analysisLogId ?? null,
  };
});

const fetchHarmonyData = async (suggestionFlag = null) => {
  if (!harmonyParams.value) return;
  isFetchingHarmony.value = true;
  try {
    // 권한(구매 OR 구독+내사주 base)이면 전체(flag 3), 아니면 무료 미리보기(flag 1).
    // BE는 비권한 조합에 flag 3 요청 시 403을 주므로 flag을 canViewFull과 일치시킨다.
    const flag = suggestionFlag ?? (canViewFull.value ? 3 : 1);
    const params = { ...harmonyParams.value, suggestionFlag: flag };
    const result = await fetchHarmonyServiceAPI(params);
    const transformed = transformHarmonyResponse(result?.data, params.category);
    dataStore.setHarmonyData(transformed);
    DEV_MODE && console.log('[궁합 모바일] fetchHarmonyData 완료:', transformed);
    DEV_MODE && console.log('[궁합 모바일] fetchHarmonyData 원본:', result?.data);
    // 구독자는 추가해석(올해/월별)을 클릭 없이 자동 조회.
    // 카테고리별 갱신을 위해 이전 추가해석 상태를 초기화 후 재조회.
    if (subscriptionCovers.value) {
      purchasedExtraIds.value = new Set();
      analysisStore.resetHarmonyExtras();
      autoLoadExtrasForSubscription();
    }
  } catch (e) {
    DEV_MODE && console.error('[궁합 모바일] fetchHarmonyData 실패:', e);
  } finally {
    isFetchingHarmony.value = false;
  }
};

// =============== 7. 분석 타입 / 가격 정보 ===============
const analysisTypeData = ref(null);
const extraTypeData = ref({ monthly: null, yearly: null });

const fetchAnalysisType = async () => {
  if (analysisTypeData.value) return;
  try {
    const data = await getAnalysisContentAPI({ analysisTypeId: ANALYSIS_TYPE_ID });
    analysisTypeData.value = data;

    const children = data?.children || [];
    const yearlyChild = children.find((c) => c.code === 'COMPAT_YEARLY');
    const monthlyChild = children.find((c) => c.code === 'COMPAT_MONTHLY');

    const extraFetches = [];
    if (monthlyChild?.analysisTypeId) {
      extraFetches.push(
        getAnalysisContentAPI({ analysisTypeId: monthlyChild.analysisTypeId })
          .then((d) => {
            extraTypeData.value.monthly = d;
          })
          .catch(() => {}),
      );
    }
    if (yearlyChild?.analysisTypeId) {
      extraFetches.push(
        getAnalysisContentAPI({ analysisTypeId: yearlyChild.analysisTypeId })
          .then((d) => {
            extraTypeData.value.yearly = d;
          })
          .catch(() => {}),
      );
    }
    await Promise.all(extraFetches);
  } catch (e) {
    DEV_MODE && console.error('[궁합 모바일] analysisType 조회 실패:', e);
  }
};

// =============== 8. 결제 관련 ===============

/** 선택된 인원수에 맞는 옵션 */
const matchedOption = computed(() => {
  const options = analysisTypeData.value?.options;
  const paymentData = harmonyPaymentData.value;
  if (!options?.length || !paymentData) return null;
  const memberCount = paymentData.memberGuestUserIds?.length || 2;
  return options.find((opt) => opt.memberCount === memberCount) || null;
});

/** 동적 가격 */
const pointPrice = computed(() => {
  if (matchedOption.value?.pointPrice) return matchedOption.value.pointPrice;
  const memberCount = harmonyPaymentData.value?.memberGuestUserIds?.length || 2;
  const basePrice = analysisTypeData.value?.pointPrice ?? 1000;
  return basePrice + Math.max(0, memberCount - 2) * 300;
});

const currentPoints = computed(() => {
  const formatted = paymentsStore?.formattedPoints || '0';
  return Number(formatted.length >= 4 ? formatted.replace(/,/g, '') : formatted);
});

const selectedMemberCount = computed(
  () => harmonyPaymentData.value?.memberGuestUserIds?.length || 2,
);

/** 인원수별 분석권 정보 */
const harmonyTickets = computed(() => paymentsStore.harmonyTicketDetails);
const matchedTicketInfo = computed(() => {
  return (
    harmonyTickets.value[`members${selectedMemberCount.value}`] || { count: 0, ticketId: null }
  );
});
const matchedTicketDisplayText = computed(() => {
  const mc = selectedMemberCount.value;
  const count = matchedTicketInfo.value.count;
  return count > 0 ? `${mc}인 ${count}건` : '0건';
});

const isPointActive = computed(() => currentPoints.value >= pointPrice.value);
const isTicketActive = computed(() => matchedTicketInfo.value.count > 0);

/** 포인트 직접 구매가의 정가 (할인 표시용) */
const pointBasePrice = computed(
  () => matchedOption.value?.pointBasePrice ?? analysisTypeData.value?.pointBasePrice ?? null,
);
const isPointDiscounted = computed(
  () => pointBasePrice.value && pointBasePrice.value > pointPrice.value,
);

const showPaymentPopup = ref(false);
const showPopAnalysisInfo = ref(false);
const buyType = ref('');

const { fetchTicketProductByTypeId } = useTicketProduct();
/** 분석권 업셀 카드용 상품 정보 ({ boardId, articleId, perUnitPrice, unit, discountPct }) */
const ticketProduct = ref(null);

const handleBuyPoint = () => {
  if (!isPointActive.value) return;
  addContentLogAPI({ contentType: 'ap_harmony_payment_opt3', option3: '포인트' });
  buyType.value = 'point';
};

/** 분석권 구매 이동 확인 팝업 ref */
const ticketConfirmRef = ref(null);

/** 업셀 카드 클릭 → 바로 이동하지 않고 작성 내용 초기화 안내 확인 팝업 표시 */
const goTicketProduct = () => {
  addContentLogAPI({ contentType: 'ap_harmony_payment_opt3', option3: '분석권상품' });
  ticketConfirmRef.value?.openConfirmDialog();
};

/** 확인(네) 클릭 → 현재 인원수에 맞는 궁합 분석권 상품 상세로 이동 (없으면 분석권 목록) */
const moveToTicketProduct = () => {
  if (ticketProduct.value?.articleId) {
    router.push(
      `/purchase/ticket-detail/${ticketProduct.value.boardId}/${ticketProduct.value.articleId}`,
    );
  } else {
    router.push('/purchase/tickets');
  }
  closePaymentPopup();
};

/** PopAnalysisInfo에 전달할 analysisType 객체 */
const popAnalysisType = computed(() => {
  const price = pointPrice.value;
  const basePrice =
    matchedOption.value?.pointBasePrice ?? analysisTypeData.value?.pointBasePrice ?? price;
  return { pointPrice: price, pointBasePrice: basePrice, name: '낭만궁합' };
});

const openPaymentPopup = async () => {
  addContentLogAPI({ contentType: 'ap_harmony_btn_start_detail' });
  await Promise.all([paymentsStore.getOwnPoint(), paymentsStore.fetchTicketCount()]);

  // 포인트 부족 + 분석권 없음 → 포인트 충전 팝업
  if (!isPointActive.value && !isTicketActive.value) {
    showPopAnalysisInfo.value = true;
    return;
  }

  // 분석권이 있으면 결제 방법 선택 UI를 건너뛰고 바로 확인 팝업
  if (isTicketActive.value) {
    buyType.value = 'ticket';
    goConfirm();
    return;
  }

  buyType.value = 'point';
  showPaymentPopup.value = true;
  // 현재 인원수에 맞는 할인 분석권 업셀 상품을 비동기로 조회
  ticketProduct.value = null;
  fetchTicketProductByTypeId(ANALYSIS_TYPE_ID, {
    memberCount: selectedMemberCount.value,
  }).then((product) => {
    ticketProduct.value = product;
  });
};

const closePaymentPopup = () => {
  addContentLogAPI({ contentType: 'ap_harmony_payment_opt3', option3: '닫기' });
  showPaymentPopup.value = false;
};

const handleClosePop = () => {
  showPopAnalysisInfo.value = false;
};

const handleChargePop = (shortageAmount) => {
  showPopAnalysisInfo.value = false;
  onRecharge(shortageAmount);
};

const goConfirm = () => {
  if (!buyType.value) return;
  addContentLogAPI({ contentType: 'ap_harmony_payment_opt3', option3: '확인' });
  showPaymentPopup.value = false;

  confirmTitle.value = '선택한 결제방법으로';
  confirmTitle2.value = '낭만궁합을 시작합니다.';
  confirmText.value = '사용';
  confirmNextUrl.value = onConfirmPayment;
  confirmDoUrl.value = onConfirmPayment;
  warningTxt.value =
    buyType.value === 'point'
      ? `포인트 ${pointPrice.value.toLocaleString()} P 사용`
      : '분석권 1건 사용';
  dialogConfirm.value.openConfirmDialog();
};

/** 결제 처리 */
const isPaymentProcessing = ref(false);

const onConfirmPayment = async () => {
  if (isPaymentProcessing.value) return;
  addContentLogAPI({ contentType: 'ap_harmony_payment_opt3', option3: '결제확정' });
  isPaymentProcessing.value = true;

  try {
    const paymentData = harmonyPaymentData.value;
    if (!paymentData) throw new Error('결제 데이터가 없습니다.');

    // analysisTypeData 미로드 시 결제 전 로드
    if (!analysisTypeData.value) {
      await fetchAnalysisType();
    }

    const params = {
      guestUserId: paymentData.memberGuestUserIds[0],
      analysisTypeId: ANALYSIS_TYPE_ID,
      paymentMethod: buyType.value === 'point' ? 'point' : 'ticket',
      optionId: matchedOption.value?.optionId,
      category: pendingCategoryPayment.value
        ? CATEGORY_API_MAP[pendingCategoryPayment.value.id]
        : paymentData.category,
      memberGuestUserIds: paymentData.memberGuestUserIds,
    };

    if (buyType.value === 'ticket') {
      let ticketId = matchedTicketInfo.value?.ticketId;
      if (!ticketId) {
        ticketId = await paymentsStore.fetchHarmonyTicketId(selectedMemberCount.value);
      }
      if (ticketId) params.analysisTicketId = ticketId;
    }

    DEV_MODE && console.log('[궁합 모바일] 결제 요청 params:', params);
    await postAnalysisLogAPI(params);

    // 분석권 결제 성공 시 서버 반영 지연 대비 낙관적 차감
    if (buyType.value === 'ticket') {
      paymentsStore.decrementHarmonyTicket(selectedMemberCount.value);
    }

    alertTitle.value = '성공적으로 결제되었습니다.';
    alertText2.value = '';
    alertNextUrl2.value = onPaymentSuccess;
    dialogAlert.value.openAlertDialog();
  } catch (e) {
    DEV_MODE && console.error('[궁합 모바일] 결제 실패:', e);
    pendingCategoryPayment.value = null;
    alertTitle.value =
      e?.response?.data?.detail || e?.response?.data?.message || '결제에 실패했습니다.';
    alertText2.value = '';
    alertNextUrl2.value = null;
    dialogAlert.value.openAlertDialog();
  } finally {
    isPaymentProcessing.value = false;
  }
};

const onPaymentSuccess = async () => {
  // 로그 재조회 → 결제 카테고리로 매칭 로그 찾기
  const paymentData = harmonyPaymentData.value;
  const selectedIds = paymentData?.memberGuestUserIds?.map(Number) || [];
  const paymentCategory = pendingCategoryPayment.value
    ? CATEGORY_API_MAP[pendingCategoryPayment.value.id]
    : paymentData?.category;

  await fetchHarmonyLogs(paymentCategory || null);

  let matched = null;
  if (paymentCategory && selectedIds.length) {
    matched =
      harmonyLogData.value.find((log) => {
        const sortedMembers = [...(log.members || [])].sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
        );
        const logIds = sortedMembers.map((m) => Number(m.guestUserId));
        if (logIds.length !== selectedIds.length) return false;
        return selectedIds.every((id, i) => id === logIds[i]);
      }) || null;
  }

  // 카테고리 재구매 시 harmonyPaymentData를 새 카테고리로 갱신
  if (pendingCategoryPayment.value) {
    analysisStore.setHarmonyPaymentData({
      category: paymentCategory,
      memberGuestUserIds: selectedIds,
      persons: paymentData?.persons || [],
    });
    pendingCategoryPayment.value = null;
  }

  analysisStore.setHarmonyPurchaseState(true, matched);
  await paymentsStore.fetchTicketCount();

  // 콘텐츠 페이지 먼저 진입 → 로딩 오버레이 표시 → 데이터 로드
  currentPage.value = 1;
  showServiceSelect.value = false;
  await fetchHarmonyData();
  loadAllCategoryLogs();
  mobileSajuLnbStore.setNeedRefresh(true);
};

/** 추가 해석 더보기 */
const extraInterpretations = computed(() => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  const children = analysisTypeData.value?.children || [];
  const yearlyChild = children.find((c) => c.code === 'COMPAT_YEARLY');
  const monthlyChild = children.find((c) => c.code === 'COMPAT_MONTHLY');

  const yearlyPrice = extraTypeData.value.yearly?.pointPrice ?? yearlyChild?.pointPrice ?? 300;
  const monthlyPrice = extraTypeData.value.monthly?.pointPrice ?? monthlyChild?.pointPrice ?? 300;
  const yearlyTypeId = yearlyChild?.analysisTypeId ?? 40;
  const monthlyTypeId = monthlyChild?.analysisTypeId ?? 39;

  const items = [
    {
      id: 'yearly',
      label: `${year}년`,
      suffix: ', 우리 사이 분위기',
      price: yearlyPrice,
      year,
      month: null,
      analysisTypeId: yearlyTypeId,
    },
  ];
  for (let i = 0; i < 3; i++) {
    const raw = month + i;
    const m = raw > 12 ? raw - 12 : raw;
    const y = raw > 12 ? year + 1 : year;
    items.push({
      id: `month${m}`,
      label: `${m}월`,
      suffix: ', 우리 사이 분위기',
      price: monthlyPrice,
      year: y,
      month: m,
      analysisTypeId: monthlyTypeId,
    });
  }
  return items;
});

/** 가격 정보를 store에 공유 (ContentsHarmony03 mood card 가격 표시용) */
watch(extraInterpretations, (items) => {
  const yearly = items.find((i) => i.id === 'yearly');
  const monthly = items.find((i) => i.id.startsWith('month'));
  analysisStore.setHarmonyExtraPrices(yearly?.price ?? 300, monthly?.price ?? 300);
}, { immediate: true });

const purchasedExtraIds = ref(new Set());

/** child log에서 extra id를 추출 */
const getExtraId = (child) => {
  if (child.year && child.month) return `month${child.month}`;
  if (child.year) return 'yearly';
  return null;
};

/** child log에서 콘텐츠 데이터를 store에 저장 */
const saveChildContent = (child) => {
  const id = getExtraId(child);
  if (!id) return;
  const content = child.result || child.data || child.content || null;
  if (content) {
    analysisStore.setHarmonyExtraContent(id, content);
  }
};

const syncPurchasedExtras = async () => {
  const matched = analysisStore.harmonyMatchedLog;

  // 이전 데이터 초기화
  analysisStore.resetHarmonyExtras();

  // 1) matchedLog의 childLogs/children에서 먼저 추출
  const childLogs = matched?.childLogs || matched?.children || [];
  const purchased = new Set();
  childLogs.forEach((child) => {
    const id = getExtraId(child);
    if (id) {
      purchased.add(id);
      saveChildContent(child);
    }
  });

  // 2) parentLogId가 있으면 API로 구매 이력 추가 조회
  if (matched?.analysisLogId && matched?.userId) {
    try {
      const res = await getAnalysisLogAPI({
        userId: matched.userId,
        includeChildTypes: true,
        isValid: true,
        parentLogId: matched.analysisLogId,
        page: 1,
        size: -1,
      });
      (res?.content || []).forEach((child) => {
        const id = getExtraId(child);
        if (id) {
          purchased.add(id);
          saveChildContent(child);
        }
      });
    } catch (e) {
      DEV_MODE && console.error('[궁합 모바일 추가해석] 구매 이력 조회 실패:', e);
    }
  }

  purchasedExtraIds.value = purchased;
  // store에 동기화
  analysisStore.setHarmonyPurchasedExtraIds(purchased);

  // 구매된 세운/월운 데이터 API 호출 (1:1일 때만)
  const memberCount = matched?.members?.length || 0;
  if (purchased.size > 0 && matched && memberCount === 2) {
    const sortedMembers = [...(matched.members || [])].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    );
    const guestUserId = sortedMembers[0]?.guestUserId;
    const now = new Date();

    for (const id of purchased) {
      if (analysisStore.harmonyExtraContents[id]) continue;
      const isYearly = id === 'yearly';
      const params = {
        guestUserId,
        category: matched.category,
        parentLogId: matched.analysisLogId,
        analysisTypeId: isYearly ? 40 : 39,
        year: now.getFullYear(),
      };
      if (!isYearly) params.month = parseInt(id.replace('month', ''));
      try {
        const result = await fetchHarmonyServiceAPI(params);
        console.log(`[궁합 모바일] ${id} API 응답:`, result?.data);
        analysisStore.setHarmonyExtraContent(id, result?.data);
      } catch (e) {
        console.error(`[궁합 모바일] ${id} API 호출 실패:`, e);
      }
    }
  }
};

watch(() => analysisStore.harmonyMatchedLog, syncPurchasedExtras, { immediate: true, deep: true });

const isExtraPurchased = (itemId) => purchasedExtraIds.value.has(itemId);

const showExtraPaymentPopup = ref(false);
const extraBuyTarget = ref(null);

/** 월 선택 팝업 */
const showMonthSelectPopup = ref(false);
const selectedMonthId = ref(null);

/** 월 선택 팝업에서 표시할 월별 항목 (구매완료 포함, 비활성화 표시) */
const monthSelectItems = computed(() => {
  return extraInterpretations.value.filter((item) => item.id.startsWith('month'));
});

/** 미구매 월별 항목만 */
const unpurchasedMonthItems = computed(() => {
  return monthSelectItems.value.filter((item) => !isExtraPurchased(item.id));
});

/** 선택된 항목이 구매 가능한지 */
const canConfirmMonth = computed(() => {
  return selectedMonthId.value && !isExtraPurchased(selectedMonthId.value);
});

const openMonthSelectPopup = () => {
  const unpurchased = unpurchasedMonthItems.value;
  selectedMonthId.value = unpurchased.length > 0 ? unpurchased[0].id : null;
  showMonthSelectPopup.value = true;
  paymentsStore.getOwnPoint();
};

const closeMonthSelectPopup = () => {
  showMonthSelectPopup.value = false;
  selectedMonthId.value = null;
};

/** 추가해석 포인트 부족 → 충전 이동 */
const goExtraChargePoint = () => {
  const price = extraBuyTarget.value?.price || 0;
  showExtraPaymentPopup.value = false;
  showMonthSelectPopup.value = false;
  router.push({
    path: '/purchase/points',
    query: { next: route.fullPath, shortageAmount: price - currentPoints.value },
  });
};

const onConfirmMonthSelect = async () => {
  if (!canConfirmMonth.value) return;
  const item = extraInterpretations.value.find((i) => i.id === selectedMonthId.value);
  if (!item) return;

  // 만 99세 초과 멤버 확인 (구독/결제 공통 — 분석 불가)
  const overAgeMembers = await getOverAgeLimitMembers();
  if (overAgeMembers.length > 0) {
    const names = overAgeMembers
      .map((m) => m.guestUserName || m.name || '')
      .filter(Boolean)
      .join(', ');
    alertTitle.value = '추가 해석을 볼 수 없습니다';
    alertText2.value = names
      ? `${names}님은 만 ${MAX_AGE_LIMIT}세를 초과하여\n추가 해석을 볼 수 없습니다.`
      : `만 ${MAX_AGE_LIMIT}세를 초과한 대상자가 포함되어\n추가 해석을 볼 수 없습니다.`;
    alertNextUrl2.value = null;
    dialogAlert.value.openAlertDialog();
    return;
  }

  // 구독이 이 조합을 커버하면 결제 없이 조회
  if (subscriptionCovers.value) {
    showMonthSelectPopup.value = false;
    await viewExtraBySubscription(item);
    return;
  }

  // 포인트 부족 체크
  const userPoints = paymentsStore?.points ?? 0;
  if (userPoints < item.price) {
    showMonthSelectPopup.value = false;
    extraBuyTarget.value = item;
    goExtraChargePoint();
    return;
  }

  // 확인 팝업 없이 바로 결제
  showMonthSelectPopup.value = false;
  extraBuyTarget.value = item;
  await onConfirmExtraPayment();
};

/** 멤버 중 만 나이 99세 초과자가 있는지 확인 */
const MAX_AGE_LIMIT = 99;

const calcRealAge = (solarStr) => {
  if (!solarStr) return null;
  const [yearStr, monthStr, dayStr] = solarStr.split('-');
  const birthYear = Number(yearStr);
  const birthMonth = Number(monthStr);
  const birthDay = Number(dayStr);
  if (!birthYear) return null;

  const now = new Date();
  let age = now.getFullYear() - birthYear;
  const todayMonth = now.getMonth() + 1;
  const todayDay = now.getDate();
  if (todayMonth < birthMonth || (todayMonth === birthMonth && todayDay < birthDay)) {
    age -= 1;
  }
  return age;
};

const getOverAgeLimitMembers = async () => {
  const persons =
    harmonyPaymentData.value?.persons || analysisStore.harmonyMatchedLog?.members || [];
  if (!persons.length) return [];

  // solar 필드가 있는 경우 바로 체크, 없으면 API로 조회
  const needsFetch = persons.some((m) => !m.solar);

  let enrichedPersons = persons;
  if (needsFetch) {
    try {
      const details = await Promise.all(
        persons.map((m) => getGuestUserDetail(m.guestUserId).catch(() => null)),
      );
      enrichedPersons = persons.map((m, i) => ({
        ...m,
        solar: m.solar || details[i]?.solar || '',
      }));
    } catch {
      DEV_MODE && console.error('[궁합 나이제한] 사주 정보 조회 실패');
      return [];
    }
  }

  return enrichedPersons.filter((m) => {
    const age = calcRealAge(m.solar);
    return age !== null && age > MAX_AGE_LIMIT;
  });
};

/** 구독자: 결제 없이 member_ids로 추가해석 조회 (구독 커버 시). parentLogId 불필요
 *  - navigate=true(직접 클릭): 로드 후 종합조언 페이지(6)로 이동
 *  - navigate=false(자동 로드): 페이지 유지 (관계 변경 시 6페이지로 튀는 문제 방지) */
const viewExtraBySubscription = async (item, { navigate = true } = {}) => {
  const fetchParams = {
    guestUserId: harmonyParams.value?.guestUserId,
    memberIds: harmonyParams.value?.memberIds,
    category: harmonyParams.value?.category,
    analysisTypeId: item.analysisTypeId,
    year: item.year,
    suggestionFlag: 3,
  };
  if (item.month) fetchParams.month = item.month;
  try {
    const res = await fetchHarmonyServiceAPI(fetchParams);
    if (res?.data) {
      analysisStore.setHarmonyExtraContent(item.id, res.data);
      purchasedExtraIds.value.add(item.id);
      analysisStore.setHarmonyPurchasedExtraIds(purchasedExtraIds.value);
      if (navigate && showAdvicePage.value) currentPage.value = 6;
    }
  } catch (e) {
    DEV_MODE &&
      console.error('[궁합 추가해석 구독조회] 실패', e?.response?.status, e?.response?.data);
  }
};

const onClickExtraItem = async (item) => {
  if (isExtraPurchased(item.id)) return;

  // 만 99세 초과 멤버 확인 (구독/결제 공통 — 분석 불가)
  const overAgeMembers = await getOverAgeLimitMembers();
  if (overAgeMembers.length > 0) {
    const names = overAgeMembers
      .map((m) => m.guestUserName || m.name || '')
      .filter(Boolean)
      .join(', ');
    alertTitle.value = '추가 해석을 볼 수 없습니다';
    alertText2.value = names
      ? `${names}님은 만 ${MAX_AGE_LIMIT}세를 초과하여\n추가 해석을 볼 수 없습니다.`
      : `만 ${MAX_AGE_LIMIT}세를 초과한 대상자가 포함되어\n추가 해석을 볼 수 없습니다.`;
    alertNextUrl2.value = null;
    dialogAlert.value.openAlertDialog();
    return;
  }

  // 구독이 이 조합을 커버하면 결제 없이 조회
  if (subscriptionCovers.value) {
    await viewExtraBySubscription(item);
    return;
  }

  extraBuyTarget.value = item;
  showExtraPaymentPopup.value = true;
  paymentsStore.getOwnPoint();
};

/** 구독자: 월별 추가해석을 월 선택 없이 전부(현재월~3개월) 병렬 조회 */
const viewAllMonthsBySubscription = async ({ navigate = true } = {}) => {
  const monthlyItems = extraInterpretations.value.filter(
    (i) => i.id.startsWith('month') && !isExtraPurchased(i.id),
  );
  // 개별 항목은 페이지 이동 안 함 → 전부 로드 후 navigate=true일 때만 한 번 이동
  await Promise.all(monthlyItems.map((item) => viewExtraBySubscription(item, { navigate: false })));
  if (navigate && monthlyItems.length && showAdvicePage.value) currentPage.value = 6;
};

/** 구독자: 궁합 결과 로드 시 추가해석(올해+월별)을 클릭 없이 병렬 자동 조회 */
const autoLoadExtrasForSubscription = async () => {
  // 만 99세 초과 멤버가 있으면 추가해석 분석 불가 → 자동조회 스킵
  const overAge = await getOverAgeLimitMembers();
  if (overAge.length > 0) return;
  analysisStore.setHarmonyExtrasAutoLoading(true);
  try {
    const yearly = extraInterpretations.value.find((i) => i.id === 'yearly');
    // 자동 로드는 페이지를 옮기지 않음 (현재 페이지 유지)
    const tasks = [viewAllMonthsBySubscription({ navigate: false })];
    if (yearly && !isExtraPurchased(yearly.id))
      tasks.push(viewExtraBySubscription(yearly, { navigate: false }));
    await Promise.all(tasks);
  } finally {
    analysisStore.setHarmonyExtrasAutoLoading(false);
  }
};

/** ContentsHarmony03 mood card에서 트리거한 추가해석 결제 요청 감시 */
watch(
  () => analysisStore.harmonyExtraPaymentTarget,
  (targetId) => {
    if (!targetId) return;
    if (targetId === 'monthly-select') {
      // 구독자는 월 선택 없이 전부 조회 (모두 무료 열람)
      if (subscriptionCovers.value) {
        viewAllMonthsBySubscription();
      } else {
        openMonthSelectPopup();
      }
      analysisStore.clearHarmonyExtraPaymentTarget();
      return;
    }
    const item = extraInterpretations.value.find((i) => i.id === targetId);
    if (item) {
      onClickExtraItem(item);
    }
    analysisStore.clearHarmonyExtraPaymentTarget();
  },
);

const closeExtraPaymentPopup = () => {
  showExtraPaymentPopup.value = false;
  extraBuyTarget.value = null;
};

const isExtraPaymentProcessing = ref(false);

const onConfirmExtraPayment = async () => {
  if (isExtraPaymentProcessing.value) return;
  isExtraPaymentProcessing.value = true;
  showExtraPaymentPopup.value = false;

  try {
    const matched = analysisStore.harmonyMatchedLog;
    if (!matched) throw new Error('매칭된 궁합 로그가 없습니다.');

    const item = extraBuyTarget.value;
    if (!item) throw new Error('결제 대상이 없습니다.');

    const sortedMembers = [...(matched.members || [])].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    );
    const primaryGuestUserId = Number(guestUserId.value) || sortedMembers[0]?.guestUserId;

    const params = {
      guestUserId: primaryGuestUserId,
      analysisTypeId: item.analysisTypeId,
      paymentMethod: 'point',
      category: matched.category || harmonyPaymentData.value?.category,
      parentLogId: matched.analysisLogId,
      year: item.year,
    };
    if (item.month) params.month = item.month;

    const postRes = await postAnalysisLogAPI(params);

    purchasedExtraIds.value.add(item.id);
    analysisStore.setHarmonyPurchasedExtraIds(purchasedExtraIds.value);

    // 응답에 콘텐츠가 포함되어 있으면 store에 저장
    const resContent = postRes?.result || postRes?.data || postRes?.content || null;
    if (resContent) {
      analysisStore.setHarmonyExtraContent(item.id, resContent);
    }

    paymentsStore.fetchTicketCount();

    // 종합 조언 오른쪽(아코디언) 페이지로 이동 (showAdvicePage=true일 때만 해당)
    if (showAdvicePage.value) currentPage.value = 6;

    alertTitle.value = '성공적으로 결제되었습니다.';
    alertText2.value = '';
    alertNextUrl2.value = null;
    dialogAlert.value.openAlertDialog();

    // 결제 API 응답에 콘텐츠가 없으면 백그라운드에서 별도 조회 (알림 차단 방지)
    if (!resContent) {
      const fetchParams = {
        guestUserId: primaryGuestUserId,
        category: matched.category || harmonyPaymentData.value?.category,
        parentLogId: matched.analysisLogId,
        analysisTypeId: item.analysisTypeId,
        year: item.year,
      };
      if (item.month) fetchParams.month = item.month;

      DEV_MODE && console.log('[궁합 모바일 추가해석] 콘텐츠 별도 조회 params:', fetchParams);
      fetchHarmonyServiceAPI(fetchParams)
        .then((fetchRes) => {
          DEV_MODE &&
            console.log(`[궁합 모바일 추가해석] ${item.id} 콘텐츠 조회 결과:`, fetchRes?.data);
          if (fetchRes?.data) {
            analysisStore.setHarmonyExtraContent(item.id, fetchRes.data);
          }
        })
        .catch((fetchErr) => {
          DEV_MODE &&
            console.error(`[궁합 모바일 추가해석] ${item.id} 콘텐츠 조회 실패:`, fetchErr);
        });
    }
  } catch (e) {
    DEV_MODE && console.error('[궁합 모바일 추가해석] 결제 실패:', e);
    alertTitle.value =
      e?.response?.data?.detail || e?.response?.data?.message || '결제에 실패했습니다.';
    alertText2.value = '';
    alertNextUrl2.value = null;
    dialogAlert.value.openAlertDialog();
  } finally {
    isExtraPaymentProcessing.value = false;
    extraBuyTarget.value = null;
  }
};

// =============== 유틸리티 ===============
const formatListDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}.${m}.${d}`;
};

// =============== 9. 페이지 상태 ===============
const currentPage = ref(0);
const showServiceSelect = ref(false);
const isRefreshingPageData = ref(false);
const harmonyServiceSelectRef = ref(null);

/** 궁합목록 바텀시트 */
const showLogSheet = ref(false);
const logSheetCategory = ref('LOVER');

useCustomScrollLock({ activeScrollLock: showLogSheet });

/** 다른 관계 살펴보기 */
const HARMONY_CATEGORIES = [
  { id: 'lover', label: '연인', emoji: '💖' },
  { id: 'friend', label: '친구', emoji: '🤝' },
  { id: 'business', label: '비즈니스', emoji: '💼' },
  { id: 'family', label: '가족', emoji: '🏠' },
];
const CATEGORY_API_MAP = {
  lover: 'LOVER',
  friend: 'FRIEND',
  business: 'BUSINESS',
  family: 'FAMILY',
};
/** API category → 한글 라벨 (로그 option3용) */
const HARMONY_LABEL_BY_API = { LOVER: '연인', FRIEND: '친구', BUSINESS: '비즈니스', FAMILY: '가족' };
const showExploreMenu = ref(false);
const pendingCategoryPayment = ref(null);

const purchasedCategoriesForPair = computed(() => {
  const matched = analysisStore.harmonyMatchedLog;
  if (!matched?.members?.length) return new Set();
  const currentIds = [...matched.members].map((m) => Number(m.guestUserId)).sort((a, b) => a - b);
  const purchased = new Set();
  for (const [catId, apiCat] of Object.entries(CATEGORY_API_MAP)) {
    const cacheKey = `${ANALYSIS_TYPE_ID}_${apiCat}`;
    const logs = analysisStore.analysisLogByType[cacheKey] || [];
    const has = logs.some((log) => {
      const logIds = [...(log.members || [])]
        .map((m) => Number(m.guestUserId))
        .sort((a, b) => a - b);
      return logIds.length === currentIds.length && currentIds.every((id, i) => id === logIds[i]);
    });
    if (has) purchased.add(catId);
  }
  return purchased;
});

const showCategoryPayConfirm = ref(false);

const onClickExploreCategory = (cat) => {
  if (purchasedCategoriesForPair.value.has(cat.id)) return;
  addContentLogAPI({ contentType: 'ap_harmony_btn_explore_category', option3: cat.label });
  showExploreMenu.value = false;
  // 구독이 이 조합을 커버하면(구매 여부 무관) 결제 없이 카테고리만 전환해 바로 조회
  if (subscriptionCovers.value) {
    switchHarmonyCategoryBySubscription(cat);
    return;
  }
  pendingCategoryPayment.value = cat;
  showCategoryPayConfirm.value = true;
};

/** 구독자: 결제 없이 같은 멤버로 카테고리만 전환해 조회 (내사주 궁합 구독 커버) */
const switchHarmonyCategoryBySubscription = async (cat) => {
  const paymentData = harmonyPaymentData.value;
  const selectedIds = paymentData?.memberGuestUserIds?.map(Number) || [];
  analysisStore.setHarmonyPaymentData({
    category: CATEGORY_API_MAP[cat.id],
    memberGuestUserIds: selectedIds,
    persons: paymentData?.persons || [],
  });
  currentPage.value = 1;
  await fetchHarmonyData();
};

const toggleExploreMenu = () => {
  if (!showExploreMenu.value) {
    addContentLogAPI({ contentType: 'ap_harmony_btn_explore_menu' });
  }
  showExploreMenu.value = !showExploreMenu.value;
};

const onConfirmCategoryPayment = () => {
  showCategoryPayConfirm.value = false;
  openPaymentPopup();
};

const onCancelCategoryPayment = () => {
  showCategoryPayConfirm.value = false;
  pendingCategoryPayment.value = null;
};

const loadAllCategoryLogs = () => {
  Object.values(CATEGORY_API_MAP).forEach((cat) => {
    const cacheKey = `${ANALYSIS_TYPE_ID}_${cat}`;
    if (!analysisStore.analysisLogByType[cacheKey]) {
      analysisStore.getAnalysisLog(null, ANALYSIS_TYPE_ID, cat);
    }
  });
};

watch(
  isPurchased,
  (val) => {
    if (val) loadAllCategoryLogs();
  },
  { immediate: true },
);

const CATEGORY_TABS = [
  { id: 'LOVER', label: '연인', emoji: '💕' },
  { id: 'FRIEND', label: '친구', emoji: '🤝' },
  { id: 'BUSINESS', label: '비즈니스', emoji: '💼' },
  { id: 'FAMILY', label: '가족', emoji: '🏠' },
];

/** 전체 typeId=38 로그 (초기 fetchHarmonyLogs() 호출 결과 - 카테고리 무관) */
const allHarmonyLogs = computed(
  () => analysisStore.analysisLogByType[ANALYSIS_TYPE_ID] || [],
);

/** 카테고리별 구매 여부 맵 (CATEGORY_TABS id 키 기준) */
const sheetCategoryPurchaseMap = computed(() => {
  const map = { LOVER: false, FRIEND: false, BUSINESS: false, FAMILY: false };
  allHarmonyLogs.value.forEach((log) => {
    if (log.category && map[log.category] !== undefined) map[log.category] = true;
  });
  return map;
});

/** 전체 카테고리 미구매 여부 */
const hasAnyHarmonyPurchase = computed(() =>
  Object.values(sheetCategoryPurchaseMap.value).some(Boolean),
);

const filteredLogData = computed(() => harmonyLogData.value);

/** 카테고리 탭 변경 시 해당 카테고리로 API 재호출 (미구매 카테고리는 무시) */
const onChangeLogCategory = (categoryId) => {
  if (!sheetCategoryPurchaseMap.value[categoryId]) return;
  logSheetCategory.value = categoryId;
  fetchHarmonyLogs(categoryId);
};

/** 궁합목록 바텀시트 열기 (현재 카테고리로 조회, 미구매 카테고리면 첫 구매 카테고리로 전환) */
const openLogSheet = () => {
  showLogSheet.value = true;
  if (!hasAnyHarmonyPurchase.value) return;
  if (!sheetCategoryPurchaseMap.value[logSheetCategory.value]) {
    const firstPurchased = CATEGORY_TABS.find(
      (tab) => sheetCategoryPurchaseMap.value[tab.id],
    );
    if (firstPurchased) logSheetCategory.value = firstPurchased.id;
  }
  fetchHarmonyLogs(logSheetCategory.value);
};

/** 궁합 분석권 수량 (인원수별 합산) */
const harmonyTicketCount = computed(() => {
  const details = paymentsStore.harmonyTicketDetails;
  return details.members2.count + details.members3.count + details.members4.count;
});

// =============== 10. 평가 ===============
const ratingResult = ref(0);
const ratingStatus = ref(false);

const getHarmonyRate = async () => {
  if (!isPurchased.value || !harmonyParams.value?.analysisLogId) return;
  try {
    const result = await getDailyFortuneRateAPI({
      serviceName: 'NANGMAN_HARMONY',
      objectKey: `${harmonyParams.value.analysisLogId}`,
    });
    if (result) {
      ratingResult.value = result.score;
      ratingStatus.value = true;
    }
  } catch (e) {
    if (e?.response?.status === 404) {
      ratingResult.value = 0;
      ratingStatus.value = false;
    }
  }
};

const postHarmonyRate = async (score) => {
  if (!harmonyParams.value?.analysisLogId) return;
  try {
    await postDailyFortuneRateAPI({
      score,
      serviceName: 'NANGMAN_HARMONY',
      objectKey: `${harmonyParams.value.analysisLogId}`,
    });
    ratingStatus.value = true;
    getHarmonyRate();
  } catch (e) {
    DEV_MODE && console.error('[궁합 모바일] 평가 실패:', e);
  }
};

// =============== 11. 초기화 ===============
const setupInit = async () => {
  if (!guestUserId.value) return;
  isRefreshingPageData.value = true;

  try {
    const result = await initWithValidation(guestUserId.value, {
      showAlert,
      onRestricted: () => router.back(),
    });
    if (result) return;

    isInitialized.value = true;

    await Promise.all([
      paymentsStore.fetchTicketCount(),
      paymentsStore.getOwnPoint(),
      fetchHarmonyLogs(),
      fetchAnalysisType(),
    ]);
    if (context && !context.isActive()) return;
    await restoreHistory();
  } catch (e) {
    DEV_MODE && console.error('[궁합 모바일] 초기화 실패:', e);
  } finally {
    isRefreshingPageData.value = false;
  }
};

const refreshPageData = async () => {
  isRefreshingPageData.value = true;
  try {
    await Promise.all([
      paymentsStore.fetchTicketCount(),
      paymentsStore.getOwnPoint(),
      fetchHarmonyLogs(),
    ]);
  } catch (e) {
    DEV_MODE && console.error('[궁합 모바일] 새로고침 실패:', e);
  } finally {
    isRefreshingPageData.value = false;
  }
};

// =============== 12. 페이지 네비게이션 ===============
const goServiceSelectPage = () => {
  showServiceSelect.value = true;
  // selectedMenu를 리셋해야 startHarmony()의 clickMainMenu('/nangman-harmony')가 watch를 트리거함
  analysisStore.resetSelectedMenu();
};

/** 낭만궁합 시작하기 버튼 클릭 → 기구매 체크 후 진입 */
const onClickStartHarmony = () => {
  if (!harmonyServiceSelectRef.value?.canStart) return;

  // 현재 store에 이미 syncPaymentData watch로 설정된 harmonyPaymentData 사용
  const paymentData = harmonyPaymentData.value;
  if (paymentData?.memberGuestUserIds?.length) {
    const selectedIds = paymentData.memberGuestUserIds.map(Number);
    const apiCategory = paymentData.category;
    const categoryLogs = harmonyLogData.value.filter((log) => log.category === apiCategory);
    const matched =
      categoryLogs.find((log) => {
        const sortedMembers = [...(log.members || [])].sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
        );
        const logIds = sortedMembers.map((m) => Number(m.guestUserId));
        if (logIds.length !== selectedIds.length) return false;
        return selectedIds.every((id, i) => id === logIds[i]);
      }) || null;

    if (matched) {
      showAlert({
        title: '이미 구매하신 궁합입니다.\n구매 데이터로 이동합니다.',
        nextUrl: () => onClickLogCard(matched),
      });
      return;
    }
  }

  // 기구매 아닌 경우 기존 플로우 실행
  harmonyServiceSelectRef.value?.startHarmony();
};

const clearHistoryRoute = () => {
  if (!route.query.analysisLogId) return;
  const query = { ...route.query };
  delete query.analysisLogId;
  delete query.menu;
  router.replace({ path: route.path, query });
};

const goToCoverPage = () => {
  clearHistoryRoute();
  analysisStore.resetSelectedMenu();
  currentPage.value = 0;
  showServiceSelect.value = false;
  analysisStore.resetHarmonyPaymentData();
  analysisStore.setHarmonyPurchaseState(false, null);
  analysisStore.requestHarmonyResetSelection();
};

/** 새 궁합 보기 → 대상자 선택 페이지로 이동 */
const goToNewHarmony = () => {
  clearHistoryRoute();
  addContentLogAPI({ contentType: 'ap_harmony_btn_explore_other' });
  currentPage.value = 0;
  showServiceSelect.value = true;
  analysisStore.resetHarmonyPaymentData();
  analysisStore.setHarmonyPurchaseState(false, null);
  analysisStore.requestHarmonyResetSelection();
  analysisStore.resetSelectedMenu();
};

const clickPrevPage = () => {
  if (isFetchingHarmony.value) return;
  if (currentPage.value <= 1) return;
  currentPage.value -= 1;
  addContentLogAPI({
    contentType: isPurchased.value ? 'ap_harmony_page_paid_opt3' : 'ap_harmony_page_free_opt3',
    option3: String(currentPage.value),
  });
};

const clickNextPage = () => {
  if (isFetchingHarmony.value) return;
  if (currentPage.value >= totalPages.value) {
    currentPage.value = 1;
    addContentLogAPI({
      contentType: isPurchased.value ? 'ap_harmony_page_paid_opt3' : 'ap_harmony_page_free_opt3',
      option3: String(currentPage.value),
    });
    return;
  }
  // Content1(page 1-2)은 무료, page 3부터는 구매(또는 구독+내사주) 필요
  if (currentPage.value >= 2 && !canViewFull.value) {
    openPaymentPopup();
    return;
  }
  currentPage.value += 1;
  addContentLogAPI({
    contentType: isPurchased.value ? 'ap_harmony_page_paid_opt3' : 'ap_harmony_page_free_opt3',
    option3: String(currentPage.value),
  });
};

/** 스와이프 제스처 */
const { handleStart, handleMove, handleEnd } = useGesture({
  onSwipeLeft: clickNextPage,
  onSwipeRight: clickPrevPage,
  SWIPE_THRESHOLD: 150,
});

/** 목차 클릭 이동 */
const updateIndexNo = (id) => {
  if (id > 2 && !canViewFull.value) {
    openPaymentPopup();
    return;
  }
  currentPage.value = id;
  closeContentsList();
};

/** 목차 리스트 토글 */
const openFlag = ref('');
const openContentsList = () => {
  openFlag.value = openFlag.value === '' ? 'open' : '';
};
const closeContentsList = () => (openFlag.value = '');

/** 충전 */
const onRecharge = (shortageAmount) => {
  router.push({ path: '/purchase/points', query: { next: route.fullPath, shortageAmount } });
};

/** 닫기 */
const close = () => {
  router.push(context ? `/m/saju-insight/${context.saju.value.guestUserId}` : '/saju-service');
};

const unwindHarmonyOne = () => {
  if (currentPage.value > 1) {
    clickPrevPage();
    return;
  }
  if (currentPage.value === 1) {
    currentPage.value = 0;
    showServiceSelect.value = true;
    return;
  }
  if (showServiceSelect.value) goToCoverPage();
};

/** 궁합 목록 카드 클릭 → 해당 로그로 콘텐츠 진입 */
const onClickLogCard = async (log) => {
  if (context && !context.isActive()) return;
  if (context && String(route.query.analysisLogId) !== String(log.analysisLogId)) {
    return context.openHistory('harmony', context.saju.value.guestUserId, log.analysisLogId);
  }
  // 유료 조회 로그(카테고리별) — 구매 카드 열람. PC CoverPanelHarmony 와 동일.
  const cardLabel = HARMONY_LABEL_BY_API[log.category] || log.category || '';
  addContentLogAPI({
    contentType: 'ap_harmony_log_card_open_opt3',
    option3: `${cardLabel},${log?.analysisLogId ?? ''}`,
  });
  // harmonyPaymentData도 클릭한 카드의 멤버 정보로 동기화 (watch의 오매칭 방지)
  const sortedMembers = [...(log.members || [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
  );
  analysisStore.setHarmonyPaymentData({
    category: log.category,
    memberGuestUserIds: sortedMembers.map((m) => Number(m.guestUserId)),
    persons: sortedMembers,
  });
  analysisStore.setHarmonyPurchaseState(true, log);
  // 콘텐츠 페이지 먼저 진입 → 로딩 오버레이 표시 → 데이터 로드
  currentPage.value = 1;
  showServiceSelect.value = false;
  await fetchHarmonyData();
  await getHarmonyRate();
};

const restoreHistory = async () => {
  if (!route.query.analysisLogId) return;
  const log = harmonyLogData.value.find((entry) => String(entry.analysisLogId) === String(route.query.analysisLogId));
  if (log) await onClickLogCard(log);
};
watch(() => route.query.analysisLogId, restoreHistory);

// =============== 13. Watch ===============

/** HarmonyServiceSelect의 startHarmony() → analysisStore.clickMainMenu('/nangman-harmony') 감지 */
watch(
  () => analysisStore.selectedMenu,
  async (newMenu) => {
    if (newMenu === '/nangman-harmony') {
      // matchedPurchaseLog 계산
      const paymentData = harmonyPaymentData.value;
      if (paymentData?.memberGuestUserIds?.length) {
        const selectedIds = paymentData.memberGuestUserIds.map(Number);
        const apiCategory = paymentData.category;
        const categoryLogs = harmonyLogData.value.filter((log) => log.category === apiCategory);
        const matched =
          categoryLogs.find((log) => {
            const sortedMembers = [...(log.members || [])].sort(
              (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
            );
            const logIds = sortedMembers.map((m) => Number(m.guestUserId));
            if (logIds.length !== selectedIds.length) return false;
            return selectedIds.every((id, i) => id === logIds[i]);
          }) || null;

        if (matched) {
          // 이미 결제된 조합 → 안내 팝업 후 콘텐츠 진입
          showAlert({
            title: '이미 구매하신 궁합입니다.\n구매 데이터로 이동합니다.',
            nextUrl: () => onClickLogCard(matched),
          });
        } else {
          // 미결제 → 무료 미리보기(Content1)로 진입, page 3+ 진입 시 결제 팝업
          analysisStore.setHarmonyPurchaseState(false, null);
          currentPage.value = 1;
          showServiceSelect.value = false;
          await fetchHarmonyData();
        }
      }
    }
  },
);

/** 페이지 전환 시 스크롤 맨 위로 초기화 */
watch(currentPage, () => {
  window.scrollTo(0, 0);
});
watch(showServiceSelect, () => {
  window.scrollTo(0, 0);
});

/** guestUserId watch */
watch(guestUserId, async (newId, oldId) => {
  if (!newId || newId === oldId) return;
  await setupInit();
});

/** mobileSajuLnbStore.guestUserId watch */
watch(
  () => mobileSajuLnbStore.guestUserId,
  (newId) => {
    if (!context && newId && newId !== guestUserId.value) {
      guestUserId.value = newId;
    }
  },
);

/** 구매 상태 변경 시 궁합 데이터 fetch */
watch(isPurchased, async (newVal) => {
  if (newVal && harmonyParams.value && isObjectEmpty(dataStore.harmonyData)) {
    await fetchHarmonyData();
    await getHarmonyRate();
  }
});

// =============== 14. 라이프사이클 ===============
onMounted(async () => {
  // 비로그인 접근 차단
  if (!userId.value && !sessionStorage.getItem('X-Token')) {
    showAlert({ title: '로그인 후 이용 가능합니다.', nextUrl: () => router.push('/auth/login') });
    return;
  }

  await nextTick();

  // mySajuId가 아직 로드되지 않았으면 조회
  if (!primarySajuStore.mySajuId) {
    await primarySajuStore.fetchMySaju();
  }

  const queryId = Number(route.query.id) || null;
  const newId = context?.saju.value?.guestUserId || queryId || mobileSajuLnbStore.guestUserId || primarySajuStore.mySajuId || null;
  if (newId) {
    mobileSajuLnbStore.setGuestUserId(newId);
  }
  if (newId) {
    guestUserId.value = null;
    await nextTick();
    guestUserId.value = newId;
  } else {
    isInitialized.value = true;
    await Promise.all([
      paymentsStore.fetchTicketCount(),
      paymentsStore.getOwnPoint(),
      fetchHarmonyLogs(),
    ]);
  }
});

useBackLayer({
  id: 'harmony-payment',
  isOpen: showPaymentPopup,
  closeFromBack: closePaymentPopup,
});
useBackLayer({
  id: 'harmony-category-payment-confirm',
  isOpen: showCategoryPayConfirm,
  closeFromBack: onCancelCategoryPayment,
});
useBackLayer({
  id: 'harmony-extra-payment',
  isOpen: computed(() => showExtraPaymentPopup.value && !!extraBuyTarget.value),
  closeFromBack: closeExtraPaymentPopup,
});
useBackLayer({
  id: 'harmony-month-select',
  isOpen: showMonthSelectPopup,
  closeFromBack: closeMonthSelectPopup,
});
useBackLayer({
  id: 'harmony-purchase-history',
  isOpen: showLogSheet,
  closeFromBack: () => {
    showLogSheet.value = false;
  },
});
useBackLayer({
  id: 'harmony-explore-menu',
  isOpen: showExploreMenu,
  closeFromBack: () => {
    showExploreMenu.value = false;
  },
});
useBackLayer({
  id: 'harmony-contents-menu',
  isOpen: computed(() => openFlag.value === 'open'),
  closeFromBack: closeContentsList,
});
useBackLayer({
  id: 'harmony-loading',
  isOpen: isFetchingHarmony,
  dismissible: false,
});
useBackReducer({
  id: 'harmony-flow',
  canUnwind: () => currentPage.value > 0 || showServiceSelect.value,
  getDepth: () => (currentPage.value > 0 ? currentPage.value + 1 : showServiceSelect.value ? 1 : 0),
  unwindOne: unwindHarmonyOne,
});

onUnmounted(() => {
  analysisStore.setNangmanHarmonyIndex(1);
  analysisStore.resetHarmonyPaymentData();
  analysisStore.setHarmonyPurchaseState(false, null);
  analysisStore.resetHarmonyExtras();
});
</script>

<template>
  <AlertComponent ref="alertRef" :title="dialogTitle" :text="dialogText" :nextUrl="dialogNextUrl" />

  <div
    class="pop-saju-reading-container mobile st-analysis-harmony"
    v-if="isInitialized"
    @mousedown="currentPage >= 1 ? handleStart($event) : null"
    @mousemove="currentPage >= 1 ? handleMove($event) : null"
    @mouseup="currentPage >= 1 ? handleEnd($event) : null"
    @touchstart="currentPage >= 1 ? handleStart($event) : null"
    @touchmove="currentPage >= 1 ? handleMove($event) : null"
    @touchend="currentPage >= 1 ? handleEnd($event) : null"
  >
    <AnalysisMobileHeader
      title="낭만궁합"
      :guestUserId="guestUserId"
      :showPersonIcon="!!guestUserId && !context"
      @close="close"
    />

    <!-- ===== 표지 / 서비스 선택 (currentPage === 0) ===== -->
    <div v-if="currentPage === 0" class="pop-ana-saju-reading-inner intro">
      <!-- 표지 -->
      <div v-if="!showServiceSelect" class="harmony-mobile-cover">
        <HarmonyCover
          :ticketCountData="null"
          :harmonyLogData="harmonyLogData"
          :hasPermission="harmonyLogData.length > 0"
          :loadingTicket="false"
          mobile
        />

        <div class="btn-footer-box">
          <button class="btn-white" @click="goServiceSelectPage">궁합 시작하기 &rarr;</button>
        </div>
      </div>

      <!-- 서비스 선택 -->
      <div v-if="showServiceSelect" class="harmony-service-select-wrap">
        <HarmonyServiceSelect ref="harmonyServiceSelectRef" mobile :guestUserId="guestUserId" />

        <!-- 하단 바: 궁합목록 / 페이지 -->
        <div class="harmony-bottom-bar">
          <button class="harmony-bottom-bar__log" @click="openLogSheet">구매한 내역</button>
          <button
            class="harmony-bottom-bar__start"
            :class="{ active: harmonyServiceSelectRef?.canStart }"
            :disabled="!harmonyServiceSelectRef?.canStart"
            @click="onClickStartHarmony"
          >
            낭만궁합 시작하기 →
          </button>
        </div>
      </div>
    </div>

    <!-- ===== 콘텐츠 페이지 (currentPage >= 1) ===== -->
    <div v-if="currentPage >= 1" class="pop-ana-saju-reading-inner st-page st-content-page">
      <AnalysisLoadingOverlay :loading="isFetchingHarmony" message="궁합 분석중입니다!" />

      <div
        v-if="!isFetchingHarmony"
        class="pop-body mobile"
        :class="{ 'show-left': isLeftSection, 'show-right': !isLeftSection }"
      >
        <ContentsHarmony01
          v-if="getContentIndex(currentPage) === 1"
          :hData="dataStore.harmonyData"
          :userName="analysisStore.userName"
          :categoryId="harmonyPaymentData?.category"
          :hasPermission="canViewFull"
          :activeSide="isLeftSection ? 'left' : 'right'"
        />

        <ContentsHarmony02
          v-if="getContentIndex(currentPage) === 2"
          :hData="dataStore.harmonyData"
          :userName="analysisStore.userName"
          :hasPermission="canViewFull"
        />

        <ContentsHarmony03
          v-if="getContentIndex(currentPage) === 3"
          :hData="dataStore.harmonyData"
          :userName="analysisStore.userName"
          :hasPermission="canViewFull"
          :subscriptionCovers="subscriptionCovers"
        />

        <ContentsHarmony04
          v-if="getContentIndex(currentPage) === 4"
          :hData="dataStore.harmonyData"
          :userName="analysisStore.userName"
          :hasPermission="canViewFull"
          :ratingResult="ratingResult"
          :ratingStatus="ratingStatus"
          @postRate="postHarmonyRate"
          @goToOverView="goToNewHarmony"
        />
      </div>

      <!-- 다른 관계 살펴보기 플로팅 버튼 + 팝업 메뉴 (구매/구독 열람 시) -->
      <div
        v-if="canViewFull && currentPage >= 1 && !isFetchingHarmony"
        class="explore-float-wrap"
      >
        <!-- 카테고리 팝업 메뉴 (위로 열림) -->
        <Transition name="explore-menu-fade">
          <div v-if="showExploreMenu" class="explore-float-menu">
            <button
              v-for="cat in HARMONY_CATEGORIES"
              :key="cat.id"
              class="explore-float-menu__item"
              :class="{
                'explore-float-menu__item--active': purchasedCategoriesForPair.has(cat.id),
                'explore-float-menu__item--disabled': purchasedCategoriesForPair.has(cat.id),
              }"
              @click="onClickExploreCategory(cat)"
            >
              <span class="explore-float-menu__emoji">{{ cat.emoji }}</span>
              <span class="explore-float-menu__label">{{ cat.label }}</span>
            </button>
          </div>
        </Transition>
        <!-- 메인 버튼 -->
        <button class="explore-float-btn" @click="toggleExploreMenu">
          <svg
            class="explore-float-btn__icon"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="6.5" cy="6.5" r="5" stroke="white" stroke-width="1.5" />
            <line
              x1="10.35"
              y1="10.35"
              x2="14.15"
              y2="14.15"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
          다른 관계 살펴보기
        </button>
      </div>
      <!-- 배경 오버레이 (메뉴 열릴 때) -->
      <div
        v-if="showExploreMenu"
        class="explore-float-backdrop"
        @click="showExploreMenu = false"
      ></div>

      <!-- 새 궁합 보기 플로팅 버튼 (미열람 상태에서만) -->
      <button
        v-if="!canViewFull && currentPage >= 1 && !isFetchingHarmony"
        class="harmony-mobile-float-btn"
        @click="goToNewHarmony"
      >
        <svg
          class="harmony-mobile-float-btn__icon"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.65 2.35C12.2 0.9 10.21 0 8 0C3.58 0 0.01 3.58 0.01 8C0.01 12.42 3.58 16 8 16C11.73 16 14.84 13.45 15.73 10H13.65C12.83 12.33 10.61 14 8 14C4.69 14 2 11.31 2 8C2 4.69 4.69 2 8 2C9.66 2 11.14 2.69 12.22 3.78L9 7H16V0L13.65 2.35Z"
            fill="white"
          />
        </svg>
        새 궁합 보기
      </button>

      <!-- 하단 네비게이션 (미열람 상태) -->
      <div v-if="!canViewFull" class="harmony-unpurchased-bar">
        <button class="harmony-unpurchased-bar__log" @click="openLogSheet">구매한 내역</button>
        <div class="harmony-unpurchased-bar__right">
          <template v-if="currentPage >= 2">
            <button class="harmony-unpurchased-bar__prev" :disabled="isFetchingHarmony" @click="clickPrevPage">←</button>
            <div class="harmony-unpurchased-bar__sep"></div>
          </template>
          <button
            class="harmony-unpurchased-bar__start"
            :disabled="isFetchingHarmony"
            @click="currentPage >= 2 ? openPaymentPopup() : clickNextPage()"
          >
            시작하기 →
          </button>
        </div>
      </div>

      <!-- 하단 네비게이션 (구매 완료) -->
      <div v-else class="pop-footer-page-box">
        <div class="pop-footer-page-box__inner">
          <button class="pop-footer-log-btn" @click="openLogSheet">구매한 내역</button>
          <div class="pop-pagination">
            <button
              class="pop-pagination__prev"
              :disabled="currentPage <= 1 || isFetchingHarmony"
              @click="clickPrevPage"
            >
              <i class="icon-chevron"></i>
            </button>
            <div class="pop-pagination__index">{{ currentPage }} / {{ totalPages }}</div>
            <button class="pop-pagination__next" :disabled="isFetchingHarmony" @click="clickNextPage">
              <i class="icon-chevron right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 하단 결제 정보 바 (표지에서만, 서비스 선택 화면에서는 숨김) -->
    <AnalysisPaymentInfoBar
      v-if="currentPage === 0 && !showServiceSelect"
      ticketLabel="궁합분석권"
      :ticketCount="harmonyTicketCount"
      :formattedPoints="paymentsStore?.formattedPoints"
      @recharge="onRecharge"
    />
  </div>

  <!-- ===== 포인트 충전 팝업 (포인트 부족 + 분석권 없음) ===== -->
  <Teleport to="body">
    <PopAnalysisInfo
      :isVisible="showPopAnalysisInfo"
      :analysisType="popAnalysisType"
      @close="handleClosePop"
      @charge="handleChargePop"
    />
  </Teleport>

  <!-- ===== 카테고리 추가 결제 확인 팝업 ===== -->
  <Teleport to="body">
    <div v-if="showCategoryPayConfirm" class="pop-analysis-info-backdrop">
      <div class="extra-pay-popup">
        <div class="extra-pay-popup__text">
          <p class="extra-pay-popup__title">
            {{ pendingCategoryPayment?.emoji }} {{ pendingCategoryPayment?.label }} 궁합
          </p>
          <p class="extra-pay-popup__desc">구매 후 열람하시겠습니까?</p>
          <p class="extra-pay-popup__price">결제 포인트 {{ pointPrice.toLocaleString() }} P</p>
        </div>
        <div class="extra-pay-popup__buttons">
          <button
            class="extra-pay-popup__btn extra-pay-popup__btn--cancel"
            @click="onCancelCategoryPayment"
          >
            취소
          </button>
          <button
            class="extra-pay-popup__btn extra-pay-popup__btn--confirm"
            @click="onConfirmCategoryPayment"
          >
            구매
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ===== 결제 팝업 ===== -->
  <Teleport to="body">
    <div v-if="showPaymentPopup" class="pop-analysis-info-backdrop">
      <div class="pop-analysis-info">
        <div class="pop-header">
          <div class="pop-title">사용하실 결제 방법을 선택해주세요!</div>
          <button class="pop-close" @click="closePaymentPopup"></button>
        </div>
        <div class="pop-info-row">
          <span class="pop-info-label">보유중</span>
          <div class="pop-info-point-row">
            <span class="pop-info-point">{{ paymentsStore?.formattedPoints }} P</span>
            <span class="pop-info-divider">|</span>
            <div class="pop-info-ticket">
              <span>궁합분석권</span>
              <span>{{ matchedTicketDisplayText }}</span>
            </div>
          </div>
        </div>
        <div class="pop-options-row">
          <div
            class="option-box point"
            :class="{ active: buyType === 'point', disabled: !isPointActive }"
            @click="handleBuyPoint"
          >
            <div class="option-icon-n"></div>
            <div class="option-desc-col">
              <div class="option-title">포인트 사용하기</div>
              <div class="option-value">
                <template v-if="isPointDiscounted">
                  <span class="option-base-price">{{ formatNumber(pointBasePrice) }} P</span>
                  <span class="option-price-arrow">→</span>
                  <span class="option-price-sale">{{ formatNumber(pointPrice) }} P</span>
                </template>
                <template v-else>{{ formatNumber(pointPrice) }} P</template>
              </div>
            </div>
          </div>
          <div class="option-box ticket-upsell" @click="goTicketProduct">
            <span
              v-if="ticketProduct?.perUnitPrice != null && ticketProduct.discountPct > 0"
              class="option-discount-badge"
              >{{ ticketProduct.discountPct }}% 할인</span
            >
            <div class="option-icon-ticket"></div>
            <div class="option-desc-col">
              <div class="option-title">할인 분석권</div>
              <div v-if="ticketProduct?.perUnitPrice != null" class="option-value">
                1{{ ticketProduct.unit }}당 {{ formatNumber(ticketProduct.perUnitPrice) }} P
              </div>
              <div v-else class="option-value upsell-fallback">상품 보기</div>
            </div>
            <span class="upsell-arrow" aria-hidden="true">›</span>
          </div>
        </div>
        <button
          class="paywall_use_button_confirm"
          :class="{ disabled: !buyType }"
          @click="goConfirm"
        >
          확인
        </button>
      </div>
      <!-- backdrop 내부에 둬야 같은 스태킹 컨텍스트에서 결제 팝업 위로 표시됨 -->
      <ConfirmComponent
        ref="ticketConfirmRef"
        title="할인 분석권 구매 화면으로 이동하면 현재 작성 중인 내용이 초기화될 수 있습니다."
        title2="이동하시겠습니까?"
        title2-class="confirm-highlight-orange"
        text="네"
        cancel-text="아니오"
        :next-url="moveToTicketProduct"
      />
    </div>

    <!-- 추가해석 결제 팝업 -->
    <div v-if="showExtraPaymentPopup && extraBuyTarget" class="pop-analysis-info-backdrop">
      <div class="extra-pay-popup">
        <div class="extra-pay-popup__text">
          <p class="extra-pay-popup__title">
            {{ extraBuyTarget.label }}{{ extraBuyTarget.suffix }}
          </p>
          <p class="extra-pay-popup__desc">구매 후 열람하시겠습니까?</p>
          <p class="extra-pay-popup__price">
            결제 포인트 {{ extraBuyTarget.price.toLocaleString() }} P
          </p>
        </div>
        <div class="extra-pay-popup__buttons">
          <button
            class="extra-pay-popup__btn extra-pay-popup__btn--cancel"
            @click="closeExtraPaymentPopup"
          >
            취소
          </button>
          <button
            class="extra-pay-popup__btn extra-pay-popup__btn--confirm"
            :class="{ disabled: currentPoints < extraBuyTarget.price }"
            @click="currentPoints >= extraBuyTarget.price && onConfirmExtraPayment()"
          >
            {{ currentPoints >= extraBuyTarget.price ? '구매' : '포인트 부족' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 월 선택 팝업 -->
    <div v-if="showMonthSelectPopup" class="pop-analysis-info-backdrop">
      <div class="month-select-popup">
        <div class="month-select-popup__header">
          <p class="month-select-popup__title">보고 싶은 달을 선택해주세요.</p>
          <button class="month-select-popup__close" @click="closeMonthSelectPopup"></button>
        </div>
        <div class="month-select-popup__body">
          <div v-if="!subscriptionCovers" class="month-select-popup__info">
            <span class="month-select-popup__info-label">보유중</span>
            <span class="month-select-popup__info-point"
              >{{ paymentsStore?.formattedPoints }} P</span
            >
          </div>
          <div class="month-select-popup__options">
            <div
              v-for="item in monthSelectItems"
              :key="item.id"
              class="month-select-popup__option"
              :class="{
                'month-select-popup__option--selected': selectedMonthId === item.id,
                'month-select-popup__option--purchased': isExtraPurchased(item.id),
              }"
              @click="!isExtraPurchased(item.id) && (selectedMonthId = item.id)"
            >
              <p class="month-select-popup__option-label">{{ item.year }}년 {{ item.month }}월</p>
              <p v-if="isExtraPurchased(item.id)" class="month-select-popup__option-purchased">
                {{ subscriptionCovers ? '열람완료' : '구매완료' }}
              </p>
              <p v-else-if="!subscriptionCovers" class="month-select-popup__option-price">
                {{ item.price }} P
              </p>
            </div>
          </div>
        </div>
        <button
          class="month-select-popup__confirm"
          :class="{ 'month-select-popup__confirm--disabled': !canConfirmMonth }"
          @click="canConfirmMonth && onConfirmMonthSelect()"
        >
          {{ subscriptionCovers ? '보기' : '결제하기' }}
        </button>
      </div>
    </div>

    <!-- Confirm / Alert Dialog -->
    <ConfirmComponentPay
      ref="dialogConfirm"
      :title="confirmTitle"
      :title2="confirmTitle2"
      :text="confirmText"
      :nextUrl="confirmNextUrl"
      :warning="warningTxt"
      :doUrl="confirmDoUrl"
      logContentType="ap_harmony_payment_final_opt3"
      logCancelOption3="취소"
      logConfirmOption3="사용"
    />
    <AlertComponent
      ref="dialogAlert"
      :title="alertTitle"
      :text="alertText2"
      :nextUrl="alertNextUrl2"
    />
  </Teleport>

  <!-- 궁합목록 바텀시트 -->
  <Teleport to="body">
    <Transition name="sheet-fade">
      <div v-if="showLogSheet" class="harmony-sheet-backdrop" @click.self="showLogSheet = false">
        <Transition name="sheet-slide">
          <div v-if="showLogSheet" class="harmony-sheet">
            <div class="harmony-sheet__handle"></div>
            <div class="harmony-sheet__header">
              <span class="harmony-sheet__title">구매한 내역</span>
              <button class="harmony-sheet__close" @click="showLogSheet = false"></button>
            </div>
            <div class="harmony-sheet__divider"></div>
            <div class="harmony-sheet__tabs">
              <button
                v-for="tab in CATEGORY_TABS"
                :key="tab.id"
                class="harmony-sheet__tab"
                :class="{
                  active: logSheetCategory === tab.id && sheetCategoryPurchaseMap[tab.id],
                  'harmony-sheet__tab--disabled': !sheetCategoryPurchaseMap[tab.id],
                }"
                :disabled="!sheetCategoryPurchaseMap[tab.id]"
                @click="onChangeLogCategory(tab.id)"
              >
                <span class="harmony-sheet__tab-emoji">{{ tab.emoji }}</span>
                {{ tab.label }}
              </button>
            </div>
            <div class="harmony-sheet__list">
              <div
                v-for="item in filteredLogData"
                :key="item.analysisLogId"
                class="harmony-sheet__card"
                :class="{
                  'harmony-sheet__card--active':
                    analysisStore.harmonyMatchedLog?.analysisLogId === item.analysisLogId,
                }"
                @click="
                  showLogSheet = false;
                  onClickLogCard(item);
                "
              >
                <div class="harmony-sheet__card-info">
                  <div class="harmony-sheet__card-name">
                    {{
                      [...(item.members || [])]
                        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                        .map((m) => {
                          const n = m.guestUserName || '-';
                          return n.length > 4 ? n.slice(0, 4) + '…' : n;
                        })
                        .join(' & ') || '궁합 분석'
                    }}
                  </div>
                  <div class="harmony-sheet__card-date">{{ formatListDate(item.createdAt) }}</div>
                  <div class="harmony-sheet__card-expiry">
                    {{ item.expiryDate ? `유효기간 ${formatListDate(item.expiryDate)}` : '소장용' }}
                  </div>
                </div>
                <i class="harmony-sheet__card-arrow"></i>
              </div>
              <div v-if="!filteredLogData.length" class="harmony-sheet__empty">
                구매내역이 없습니다
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss">
@import '@/assets/nangman_style/popup/saju-reading_mobile.scss';
@import '@/assets/nangman_style/mobile/saju-reading-common.scss';
@import '@/assets/nangman_style/mobile/saju-reading-common-page.scss';
@import '@/assets/nangman_style/_analysis_main_color.scss';

/* 네이버 앱 주소창 오버레이 방지:
   래퍼 레벨 스타일은 레이아웃(m-saju-insight.vue)에서 일괄 처리 */
.pop-saju-reading-container.mobile.st-analysis-harmony {
  height: auto;
  overflow: visible;

  /* 헤더를 상단에 고정 (문서 스크롤 시에도 보이도록) */
  > .header {
    position: sticky;
    top: 0;
    z-index: 20;
    background-color: #fff;
  }

  .pop-ana-saju-reading-inner {
    height: auto;
    min-height: calc(100vh - 52px - var(--safe-area-top, 0px));
    overflow: visible;

    &.intro {
      overflow: visible;
    }

    &.st-page {
      height: auto;
      min-height: calc(100vh - 52px - var(--safe-area-top, 0px));
      overflow: visible;
      padding-bottom: 0;
    }

    &.st-content-page {
      background-color: #fff !important;
      padding-bottom: calc(52px + var(--safe-area-bottom, 0px));
      overflow-x: hidden !important;
      display: flex;
      flex-direction: column;
    }
  }

  .pop-body {
    height: auto;
    padding: 0;
    flex: 1;
    overflow: visible;

    .v-row {
      margin: 0 !important;
    }

    &.rating {
      height: 100%;
    }

    /* 모바일 좌/우 페이지 분리 */
    &.show-left {
      .no-pad-col.mobile-second {
        display: none !important;
      }
    }

    &.show-right {
      .no-pad-col.mobile-first {
        display: none !important;
      }
      /* 플로팅 버튼(다른 관계 살펴보기)과 겹침 방지 */
      padding-bottom: calc(60px + var(--safe-area-bottom, 0px));
    }
  }
}

.harmony-mobile-cover {
  display: flex;
  flex-direction: column;

  .btn-footer-box {
    position: fixed;
    bottom: calc(46px + var(--safe-area-bottom, 0px));
    left: 0;
    right: 0;
    padding: 16px;
    z-index: 10;

    .btn-white {
      display: flex;
      width: 100%;
      height: 48px;
      padding: 14px 20px;
      justify-content: center;
      align-items: center;
      gap: 4px;
      border-radius: 6px;
      border: 1px solid var(--Gray-300, #ebebeb);
      background: var(--gray-100-white, #fff);
      box-shadow: 0px -10px 10px 0px rgba(0, 0, 0, 0.05);
      color: var(--Orange-400, #ff6b6b);
      text-align: center;
      font-family: Pretendard;
      font-size: 15px;
      font-weight: 700;
      line-height: 20px;
      cursor: pointer;
    }
  }
}

/* 서비스 선택 래퍼 */
.harmony-service-select-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* 하단 바 */
.harmony-bottom-bar {
  display: flex;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 50;
  height: calc(52px + var(--safe-area-bottom, 0px));
  padding-bottom: calc(var(--safe-area-bottom, 0px));
  background: #333;

  &__log {
    flex: 0 0 188px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 14px 16px;
    background: var(--orange-300-main, #eb4c10);
    color: #fff;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    border: none;
    cursor: pointer;
  }

  &__start {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 14px 20px;
    background: var(--Gray-400, #c2c2c2);
    color: #fff;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    border: none;
    cursor: not-allowed;
    transition: background 0.2s ease;

    &.active {
      background: #333;
      cursor: pointer;
    }
  }
}

/* 컨텐츠 하단 페이지네이션 + 궁합목록 */
.pop-footer-page-box {
  &__inner {
    display: flex;
    width: 100%;
  }
}

.pop-footer-log-btn {
  display: flex;
  width: 50%;
  height: 52px;
  align-items: center;
  justify-content: center;
  background: var(--orange-300-main, #eb4c10);
  color: #fff;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
}

/* 바텀시트 backdrop */
.harmony-sheet-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9998;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

/* 바텀시트 */
.harmony-sheet {
  width: 100%;
  max-width: 500px;
  height: calc(100vh - 60px);
  height: calc(100dvh - 60px);
  background: #fff;
  border-radius: 20px 20px 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__handle {
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: var(--Gray-400, #c2c2c2);
    margin: 12px auto 0;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px 8px;
  }

  &__title {
    color: var(--orange-300-main, #eb4c10);
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
  }

  &__close {
    width: 24px;
    height: 24px;
    background: url('@manseImages/icon_close_1.svg') center/24px no-repeat;
    border: none;
    cursor: pointer;
  }

  &__divider {
    height: 2px;
    width: 76px;
    background: var(--orange-300-main, #eb4c10);
    margin: 0 0 0 20px;
  }

  &__tabs {
    display: flex;
    gap: 8px;
    padding: 16px 20px 12px;
    flex-shrink: 0;
    width: 100%;
    box-sizing: border-box;
  }

  &__tab {
    display: flex;
    flex: 1 1 0;
    min-width: 0;

    &:nth-child(3) {
      flex-grow: 1.5;
    }
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 6px;
    border-radius: 100px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: var(--Gray-200, #f8f8f8);
    color: var(--Gray-600, #757575);
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    line-height: 20px;
    white-space: nowrap;
    cursor: pointer;

    &.active {
      border-color: var(--orange-300-main, #eb4c10);
      color: var(--orange-300-main, #eb4c10);
      font-weight: 700;
      background: var(--Orange-100, #fff8f5);
    }

    &--disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__tab-emoji {
    font-size: 18px;
    line-height: 1;
  }

  &__list {
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 0 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border: 1px solid var(--Gray-300, #ebebeb);
    border-radius: 10px;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.2s ease;

    &--active {
      border-color: var(--Orange-200, #ff8800);
      background: var(--Orange-100, #fff8f5);
    }
  }

  &__card-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__card-name {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
  }

  &__card-date {
    color: var(--Gray-800, #333);
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
  }

  &__card-expiry {
    color: var(--orange-300-main, #eb4c10);
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
  }

  &__card-arrow {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    background: url('@manseImages/icon_info_detail.png') center/20px no-repeat;
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 40px 20px;
    border: 1px solid var(--Gray-300, #ebebeb);
    border-radius: 10px;
    background: #fff;
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
  }
}

/* 바텀시트 트랜지션 */
.sheet-fade-enter-active,
.sheet-fade-leave-active {
  transition: opacity 0.25s ease;
}
.sheet-fade-enter-from,
.sheet-fade-leave-to {
  opacity: 0;
}

.sheet-slide-enter-active {
  transition: transform 0.3s ease-out;
}
.sheet-slide-leave-active {
  transition: transform 0.25s ease-in;
}
.sheet-slide-enter-from,
.sheet-slide-leave-to {
  transform: translateY(100%);
}

/* 미구매 상태 하단 바 */
.harmony-unpurchased-bar {
  display: flex;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 50;
  height: 52px;

  &__log {
    flex: 0 0 168px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 14px 16px;
    background: var(--orange-300-main, #eb4c10);
    color: #fff;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    border: none;
    cursor: pointer;
  }

  &__right {
    flex: 1;
    display: flex;
    align-items: center;
    background: #333;
  }

  &__prev {
    flex: 0 0 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: #fff;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    border: none;
    cursor: pointer;
    padding: 0;
    touch-action: manipulation;
  }

  &__sep {
    flex-shrink: 0;
    width: 1px;
    height: 12px;
    background: rgba(255, 255, 255, 0.5);
  }

  &__start {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: #fff;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    border: none;
    cursor: pointer;
    padding: 0;
    touch-action: manipulation;
  }
}

/* 새 궁합 보기 플로팅 버튼 */
.harmony-mobile-float-btn {
  position: fixed;
  bottom: 62px;
  right: 16px;
  z-index: 49;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 10px 12px;
  border-radius: 60px;
  border: none;
  background: #ff6b6b;
  color: #fff;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  box-shadow: 2px 2px 10px 0px rgba(255, 107, 107, 0.2);
  cursor: pointer;
  white-space: nowrap;

  &__icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
}

/* 다른 관계 살펴보기 플로팅 버튼 + 팝업 메뉴 */
.explore-float-backdrop {
  position: fixed;
  inset: 0;
  z-index: 48;
}

.explore-float-wrap {
  position: fixed;
  bottom: calc(62px + var(--safe-area-bottom, 0px));
  right: 16px;
  z-index: 49;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.explore-float-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 10px 12px;
  border-radius: 60px;
  border: none;
  background: #ff6b6b;
  color: #fff;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  box-shadow: 2px 2px 10px 0px rgba(255, 107, 107, 0.2);
  cursor: pointer;
  white-space: nowrap;

  &__icon {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
}

.explore-float-menu {
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: flex-end;

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 60px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: #fff;
    box-shadow: 2px 2px 10px 0px rgba(143, 143, 143, 0.2);
    cursor: pointer;
    white-space: nowrap;
    min-width: 92px;

    &--active {
      background: #ebebeb;
      border-color: transparent;
    }

    &--disabled {
      cursor: default;
      opacity: 1;
    }
  }

  &__emoji {
    font-size: 18px;
    line-height: 18px;
  }

  &__label {
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    color: var(--Gray-600, #757575);
  }

  .explore-float-menu__item--active .explore-float-menu__label {
    color: var(--Gray-500, #949494);
  }
}

/* 다른 관계 살펴보기 팝업 메뉴 트랜지션 */
.explore-menu-fade-enter-active,
.explore-menu-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.explore-menu-fade-enter-from,
.explore-menu-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* 결제 팝업 스타일 */
.pop-analysis-info-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 카테고리 추가 결제 확인 팝업 */
.extra-pay-popup {
  background: var(--gray-100-white, #fff);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 325px;

  &__text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
    text-align: center;
    padding: 28px 16px;
    font-size: 15px;
    line-height: 24px;
  }

  &__title {
    color: var(--Gray-800, #333);
    font-weight: 700;
  }

  &__desc {
    color: var(--Gray-800, #333);
    font-weight: 400;
  }

  &__price {
    color: var(--orange-300-main, #eb4c10);
    font-weight: 700;
  }

  &__buttons {
    display: flex;
    width: 100%;
  }

  &__btn {
    flex: 1;
    height: 48px;
    border: none;
    cursor: pointer;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    text-align: center;

    &--cancel {
      background: var(--Gray-200, #f8f8f8);
      color: var(--Gray-800, #333);
      border-radius: 0 0 0 16px;
    }

    &--confirm {
      background: var(--orange-300-main, #eb4c10);
      color: var(--gray-100-white, #fff);
      border-radius: 0 0 16px 0;
    }
  }
}

.pop-analysis-info {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  width: calc(100% - 32px);
  max-width: 400px;
  padding: 30px 24px 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pop-header {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 18px;

  .pop-close {
    position: absolute;
    right: 16px;
    top: 30px;
  }
}

.pop-title {
  color: var(--Gray-800, #333);
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}

.pop-close {
  background: url('@manseImages/icon_close_1.svg');
  background-size: 24px 24px;
  border: none;
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.pop-info-row {
  display: flex;
  padding: 12px 20px;
  align-items: center;
  align-self: stretch;
  background: var(--Gray-200, #f8f8f8);
  color: var(--Gray-700, #575757);
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  margin-bottom: 12px;
  position: relative;
}

.pop-info-label {
  color: var(--orange-300-main, #eb4c10);
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
}

.pop-info-point-row {
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
}

.pop-info-point {
  flex: 1;
  text-align: right;
}

.pop-info-divider {
  color: #d9d9d9;
  margin: 0 12px;
}

.pop-info-ticket {
  flex: 1;
  display: flex;
  justify-content: space-between;
}

.pop-options-row {
  width: 100%;
  display: flex;
  gap: 12px;
  margin-bottom: 28px;
}

.option-box {
  position: relative;
  flex: 1;
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0 16px;
  color: var(--Gray-800, #333);
  gap: 12px;
  cursor: pointer;
  box-sizing: border-box;

  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &.active {
    border: 2px solid var(--orange-300-main, #eb4c10) !important;
    background: var(--Orange-100, #fff8f5);
    margin: -1px;
    color: var(--orange-300-main, #eb4c10);
  }
}

.option-base-price {
  color: var(--Gray-600, #757575);
  font-size: 13px;
  font-weight: 600;
  text-decoration-line: line-through;
}
.option-price-arrow {
  margin: 0 4px;
  color: var(--Gray-500, #949494);
  font-size: 13px;
  font-weight: 600;
}
.option-price-sale {
  color: var(--orange-300-main, #eb4c10);
  font-weight: 700;
}
.option-discount-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  height: 18px;
  padding: 3px 6px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background: #eb4c10;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 12px;
}
.upsell-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--Gray-500, #949494);
  font-size: 22px;
  font-weight: 400;
  line-height: 1;
}
.upsell-fallback {
  color: var(--orange-300-main, #eb4c10);
}

.option-icon-n {
  width: 40px;
  height: 40px;
  background: url('@manseImages/icon_an_guide_point.png') no-repeat center/cover;
  margin-bottom: 4px;
}

.option-icon-ticket {
  width: 40px;
  height: 40px;
  background: url('@manseImages/icon_an_guide_Frame.png') no-repeat center/cover;
  margin-bottom: 4px;
}

.option-desc-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.option-title {
  color: var(--Gray-800, #333);
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
}

.option-value {
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
}

.paywall_use_button_confirm {
  width: 100%;
  display: flex;
  padding: 14px 20px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  border: none;
  background: var(--orange-300-main, #eb4c10);
  cursor: pointer;
  color: var(--gray-100-white, #fff);
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;

  &.disabled {
    background: var(--Gray-500, #949494);
    cursor: not-allowed;
  }
}

/* 추가해석 결제 팝업 (Figma 디자인) */
.extra-pay-popup {
  background: var(--gray-100-white, #fff);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 325px;

  &__text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
    text-align: center;
    padding: 28px 16px;
    font-size: 15px;
    line-height: 24px;
  }

  &__title {
    color: var(--Gray-800, #333);
    font-weight: 700;
  }

  &__desc {
    color: var(--Gray-800, #333);
    font-weight: 400;
  }

  &__price {
    color: var(--orange-300-main, #eb4c10);
    font-weight: 700;
  }

  &__buttons {
    display: flex;
    width: 100%;
  }

  &__btn {
    flex: 1;
    height: 48px;
    border: none;
    cursor: pointer;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    text-align: center;

    &--cancel {
      background: var(--Gray-200, #f8f8f8);
      color: var(--Gray-800, #333);
      border-radius: 0 0 0 16px;
    }

    &--confirm {
      background: var(--orange-300-main, #eb4c10);
      color: var(--gray-100-white, #fff);
      border-radius: 0 0 16px 0;

      &.disabled {
        background: var(--Gray-500, #949494);
        cursor: not-allowed;
      }
    }
  }
}

/* 월 선택 팝업 */
.month-select-popup {
  background: var(--gray-100-white, #fff);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: calc(100vw - 48px);
  max-width: 400px;
  padding-block: 20px 32px;
  gap: 12px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 100%;
    height: 44px;
  }

  &__title {
    color: var(--Gray-800, #333);
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    text-align: center;
  }

  &__close {
    position: absolute;
    right: 16px;
    top: 10px;
    width: 24px;
    height: 24px;
    background: none;
    border: none;
    cursor: pointer;

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 14px;
      height: 2px;
      background: var(--Gray-800, #333);
      border-radius: 1px;
    }

    &::before {
      transform: translate(-50%, -50%) rotate(45deg);
    }

    &::after {
      transform: translate(-50%, -50%) rotate(-45deg);
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
    width: calc(100% - 48px);
  }

  &__info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 12px 16px;
    border-radius: 8px;
    background: var(--Gray-200, #f8f8f8);
    box-sizing: border-box;
  }

  &__info-label {
    color: var(--orange-300-main, #eb4c10);
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
  }

  &__info-point {
    color: var(--Gray-700, #575757);
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
  }

  &__options {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 8px;
  }

  &__option {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 16px 8px;
    border-radius: 12px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: var(--gray-100-white, #fff);
    flex: 1;
    min-width: 0;
    box-sizing: border-box;
    cursor: pointer;
    transition: all 0.2s;

    &--selected {
      border-color: var(--orange-300-main, #eb4c10);
      background: var(--Orange-100, #fff8f5);
    }

    &--selected &-label {
      color: var(--orange-300-main, #eb4c10);
      font-weight: 700;
    }

    &--selected &-price {
      color: var(--orange-300-main, #eb4c10);
    }

    &--purchased {
      background: var(--Gray-200, #f8f8f8);
      border-color: transparent;
      cursor: default;
    }

    &--purchased &-label {
      color: var(--Gray-600, #757575);
      font-weight: 600;
    }
  }

  &__option-label {
    color: var(--Gray-800, #333);
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    text-align: center;
    white-space: nowrap;
  }

  &__option-price {
    color: var(--Gray-800, #333);
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }

  &__option-purchased {
    color: var(--Gray-600, #757575);
    font-size: 14px;
    font-weight: 600;
    line-height: 24px;
  }

  &__confirm {
    display: flex;
    align-items: center;
    justify-content: center;
    width: calc(100% - 48px);
    padding: 14px 20px;
    border-radius: 6px;
    background: var(--orange-300-main, #eb4c10);
    color: var(--gray-100-white, #fff);
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    border: none;
    cursor: pointer;
    margin-top: 10px;

    &:hover {
      opacity: 0.9;
    }

    &--disabled {
      background: var(--Gray-500, #949494);
      cursor: default;

      &:hover {
        opacity: 1;
      }
    }
  }
}
</style>
